import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import NotificationSettings from '../components/NotificationSettings';
import { useAuth } from '../contexts/AuthContext';
import { 
  Bell, 
  User, 
  Gear, 
  Buildings,
  Sparkle,
  Flag,
} from '@phosphor-icons/react';
import IASettings from '../components/ia/IASettings';
import { EntreePremierJour } from '../components/premier-jour/CartePremierJour';
import { FONCTIONS, libelleRole } from '../lib/libelles';

// RP03 / ADM-R1 / PLA-30 — « Notifications & Emails » et « Cabinet » ne sont
// utilisables que par l'admin : ils ne sont plus proposés aux autres rôles, et
// l'onglet ouvert par défaut est « Mon profil » pour eux (plus de page vide).
const settingsTabs = [
  // RP87-88 — « E-mails automatiques » : 5 interrupteurs métier, liste détaillée dans « Avancé »
  { id: 'notifications', name: FONCTIONS.emailsAutomatiques, icon: Bell, adminOnly: true },
  { id: 'profil', name: 'Mon profil', icon: User },
  { id: 'cabinet', name: 'Cabinet', icon: Buildings, adminOnly: true },
  // RP84-85 — « Utilisateurs » et « Accès & Invitations » ont rejoint l'écran unique
  // « Équipe & accès » (/equipe-acces).
  // R7 — RP96/97 (D35) réglage IA du cabinet ; RP89-92 (D11) assistant « Premier jour »
  { id: 'ia', name: 'Assistant IA', icon: Sparkle, adminOnly: true },
  { id: 'premier-jour', name: 'Premier jour', icon: Flag, adminOnly: true },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState(isAdminUser ? 'notifications' : 'profil');
  // L'utilisateur peut être chargé après le premier rendu : on ne laisse jamais
  // un non-admin sur un onglet qui lui est réservé.
  useEffect(() => {
    const tab = settingsTabs.find((t) => t.id === activeTab);
    if (tab?.adminOnly && user && !isAdminUser) setActiveTab('profil');
  }, [user, isAdminUser, activeTab]);

  // Fluidité B2 (P1#26) — pour un non-admin, « Paramètres » ne montrait que « Mon profil »,
  // doublon de « Mon compte » : la section « Administration » n'est plus dans son menu
  // et un ancien lien vers /settings l'amène sur « Mon compte » (téléphone et SMS compris).
  if (user && !isAdminUser) return <Navigate to="/mon-compte" replace />;

  return (
    <Layout>
      <div className="p-6" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <Gear size={24} className="text-[#C75B39]" />
            <h1 className="text-2xl font-bold text-[#2C2C2C]" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Paramètres
            </h1>
          </div>
          <p className="text-sm text-[#6B6B6B]">
            {isAdminUser ? 'Gérez les notifications, votre profil et les paramètres du cabinet' : 'Gérez votre profil'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {settingsTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            if (tab.adminOnly && user?.role !== 'admin') return null;
            return (
              <button
                key={tab.id}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-colors ${isActive ? 'border-[#2C2C2C] bg-[#2C2C2C] text-white' : 'border-[#E0E0DB] bg-white text-[#6B6B6B] hover:border-[#2C2C2C] hover:text-[#2C2C2C]'}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`settings-tab-${tab.id}`}
              >
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {activeTab === 'notifications' && user?.role === 'admin' && (
          <NotificationSettings />
        )}

        {activeTab === 'profil' && (
          <ProfileSettings />
        )}

        {activeTab === 'cabinet' && user?.role === 'admin' && (
          <CabinetSettings />
        )}

        {activeTab === 'ia' && user?.role === 'admin' && <IASettings />}
        {activeTab === 'premier-jour' && user?.role === 'admin' && <EntreePremierJour />}
      </div>
    </Layout>
  );
}

function ProfileSettings() {
  const { user, checkAuth } = useAuth();
  const [phone, setPhone] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  // PLA-07 — pré-remplir depuis le profil enregistré (sinon « Enregistrer » écrase par du vide)
  useEffect(() => {
    if (user) {
      setPhone(user.phone || '');
      setSmsEnabled(!!user.sms_notifications_enabled);
    }
  }, [user]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { default: axios } = await import('axios');
      await axios.put(`${API_URL}/api/users/profile`, { phone, sms_notifications_enabled: smsEnabled }, { withCredentials: true });
      const { toast } = await import('sonner');
      toast.success('Profil mis à jour');
      if (checkAuth) checkAuth();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6" data-testid="profile-settings">
      <div className="border border-[#E0E0DB] bg-white p-6 space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Informations</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Nom</label>
            <p className="text-sm text-[#2C2C2C] px-3 py-2 bg-[#F5F5F3] border border-[#E0E0DB]">{user?.name}</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Email</label>
            <p className="text-sm text-[#2C2C2C] px-3 py-2 bg-[#F5F5F3] border border-[#E0E0DB]">{user?.email}</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Rôle</label>
            <p className="text-sm text-[#2C2C2C] px-3 py-2 bg-[#F5F5F3] border border-[#E0E0DB]">{libelleRole(user?.role)}</p>
          </div>
        </div>
      </div>

      <div className="border border-[#E0E0DB] bg-white p-6 space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Notifications SMS</h3>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Téléphone</label>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+33 6 12 34 56 78"
            className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]"
            data-testid="profile-phone-input"
          />
          <p className="text-xs text-[#9A9A9A] mt-1">Utilisé pour les alertes SMS urgentes uniquement</p>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#2C2C2C]">Recevoir les SMS urgents</p>
            <p className="text-xs text-[#6B6B6B]">Réserves urgentes, actions prioritaires, rappels de réunion</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={smsEnabled}
              onChange={e => setSmsEnabled(e.target.checked)}
              className="sr-only peer"
              data-testid="profile-sms-toggle"
            />
            <div className="w-9 h-5 bg-[#E0E0DB] peer-checked:bg-[#2D6A4F] rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
          </label>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#2C2C2C] hover:bg-[#4A4A4A] text-white text-sm font-medium py-2 transition-colors disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}


function CabinetSettings() {
  const [form, setForm] = useState({ nom: '', adresse: '', siret: '', email_contact: '', telephone: '', couleur_primaire: '#C4773B', couleur_secondaire: '#1E3A5F', logo_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const logoInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { default: axios } = await import('axios');
        const { data } = await axios.get(`${API_URL}/api/admin/cabinet`, { withCredentials: true });
        setForm({
          nom: data.nom || '',
          adresse: data.adresse || '',
          siret: data.siret || '',
          email_contact: data.email_contact || '',
          telephone: data.telephone || '',
          site_web: data.site_web || '',
          forme_juridique: data.forme_juridique || '',
          ordre_architectes: data.ordre_architectes || '',
          rcs: data.rcs || '',
          siege: data.siege || '',
          couleur_primaire: data.couleur_primaire || data.couleur_principale || '#C4773B',
          couleur_secondaire: data.couleur_secondaire || '#1E3A5F',
          logo_url: data.logo_url || '',
          // RP35 / D5 — défaut B : comme l'architecte (sauf équipe et suppression)
          droits_chef_de_projet: data.droits_chef_de_projet || 'comme_architecte',
        });
      } catch { /* */ } finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { default: axios } = await import('axios');
      await axios.put(`${API_URL}/api/admin/cabinet`, form, { withCredentials: true });
      const { toast } = await import('sonner');
      toast.success('Paramètres du cabinet sauvegardés');
    } catch {
      const { toast } = await import('sonner');
      toast.error('Erreur');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      const { toast } = await import('sonner');
      toast.error('Logo trop volumineux (max 2 Mo)');
      return;
    }
    setUploadingLogo(true);
    try {
      const { default: axios } = await import('axios');
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/admin/cabinet/logo`, fd, { withCredentials: true });
      setForm(f => ({
        ...f,
        logo_url: data.logo_url,
        couleur_primaire: data.couleur_primaire || f.couleur_primaire,
        couleur_secondaire: data.couleur_secondaire || f.couleur_secondaire,
      }));
      // Preview local
      const reader = new FileReader();
      reader.onload = e => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
      const { toast } = await import('sonner');
      toast.success('Logo uploadé' + (data.couleur_primaire ? ' — couleurs extraites automatiquement' : ''));
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const logoDisplayUrl = logoPreview || (form.logo_url ? `${API_URL}${form.logo_url}?t=${Date.now()}` : null);

  if (loading) return <div className="space-y-3"><div className="h-10 bg-[#F5F5F3] animate-pulse" /><div className="h-10 bg-[#F5F5F3] animate-pulse" /></div>;

  return (
    <div className="max-w-lg" data-testid="cabinet-settings">
      <div className="border border-[#E0E0DB] bg-white p-6 space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B]">Identité visuelle</h3>

        {/* Logo upload + preview */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">Logo du cabinet</label>
          <div className="flex items-center gap-4">
            <div
              className="w-24 h-24 border border-[#E0E0DB] flex items-center justify-center bg-[#FAFAF9] overflow-hidden flex-shrink-0"
              style={{ borderRadius: 8 }}
            >
              {logoDisplayUrl ? (
                <img src={logoDisplayUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="cabinet-logo-preview" />
              ) : (
                <span className="text-xs text-[#9A9A9A] text-center px-2">Aucun logo</span>
              )}
            </div>
            <div className="flex-1">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="cabinet-logo-input"
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="px-4 py-2 border border-[#2C2C2C] text-sm font-medium hover:bg-[#F5F5F3] disabled:opacity-50"
                data-testid="cabinet-logo-upload-btn"
              >
                {uploadingLogo ? 'Upload en cours...' : (form.logo_url ? 'Changer le logo' : 'Choisir un logo')}
              </button>
              <p className="text-xs text-[#9A9A9A] mt-2">PNG, JPG, SVG, WEBP — 2 Mo max</p>
              <p className="text-xs text-[#6B6B6B] mt-1">Utilisé dans les emails et les documents générés</p>
            </div>
          </div>
        </div>

        {/* Couleurs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Couleur primaire</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.couleur_primaire || '#C4773B'} onChange={e => setForm({ ...form, couleur_primaire: e.target.value })} className="w-12 h-[38px] border border-[#E0E0DB] cursor-pointer p-0" data-testid="cabinet-couleur-primaire" />
              <input type="text" value={form.couleur_primaire || ''} onChange={e => setForm({ ...form, couleur_primaire: e.target.value })} className="flex-1 px-2 py-2 border border-[#E0E0DB] text-xs font-mono focus:outline-none focus:border-[#2C2C2C]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Couleur secondaire</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.couleur_secondaire || '#1E3A5F'} onChange={e => setForm({ ...form, couleur_secondaire: e.target.value })} className="w-12 h-[38px] border border-[#E0E0DB] cursor-pointer p-0" data-testid="cabinet-couleur-secondaire" />
              <input type="text" value={form.couleur_secondaire || ''} onChange={e => setForm({ ...form, couleur_secondaire: e.target.value })} className="flex-1 px-2 py-2 border border-[#E0E0DB] text-xs font-mono focus:outline-none focus:border-[#2C2C2C]" />
            </div>
          </div>
        </div>
        <p className="text-xs text-[#9A9A9A]">Les couleurs sont extraites automatiquement du logo, mais vous pouvez les ajuster.</p>

        {/* Preview email */}
        <div className="border border-[#E0E0DB]" style={{ borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: form.couleur_primaire || '#C4773B', padding: 14, textAlign: 'center' }}>
            {logoDisplayUrl ? (
              <img src={logoDisplayUrl} alt="" style={{ maxHeight: 32, maxWidth: 140 }} />
            ) : (
              <span style={{ color: 'white', fontSize: 14, fontWeight: 700, fontFamily: 'Georgia, serif' }}>{form.nom || 'Nom du cabinet'}</span>
            )}
          </div>
          <div style={{ padding: 14, background: 'white' }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: form.couleur_secondaire || '#1E3A5F', margin: 0 }}>Aperçu email</p>
            <p style={{ fontSize: 11, color: '#6B6B6B', marginTop: 4 }}>Voici comment vos emails apparaîtront aux destinataires.</p>
          </div>
        </div>

        <div className="border-t border-[#E0E0DB] pt-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">Informations légales</h3>
          <p className="text-xs text-[#9A9A9A] mb-3">Apparaissent en pied de page de tous les documents exportés (PDF, Word, Excel) et dans le footer des emails.</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Nom du cabinet</label>
            <input value={form.nom || ''} onChange={e => setForm({ ...form, nom: e.target.value })} className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-nom" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Forme juridique</label>
            <input value={form.forme_juridique || ''} onChange={e => setForm({ ...form, forme_juridique: e.target.value })} placeholder="Ex: SAS d'architecture" className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-forme-juridique" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Ordre des architectes</label>
            <textarea value={form.ordre_architectes || ''} onChange={e => setForm({ ...form, ordre_architectes: e.target.value })} placeholder="Ex: inscrite au Tableau de l'Ordre des architectes de la région ÎLE-DE-FRANCE, sous le n° d'Ordre S2555" rows={2} className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C] resize-none" data-testid="cabinet-ordre" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Immatriculation RCS</label>
            <input value={form.rcs || ''} onChange={e => setForm({ ...form, rcs: e.target.value })} placeholder="Ex: Société inscrite au RCS de Paris sous le n° 940 979 701" className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-rcs" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Siège social</label>
            <input value={form.siege || form.adresse || ''} onChange={e => setForm({ ...form, siege: e.target.value })} placeholder="Ex: 25 Rue de Ponthieu, 75008 Paris" className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-siege" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">SIRET <span className="text-[#9A9A9A] font-normal normal-case">(optionnel)</span></label>
            <input value={form.siret || ''} onChange={e => setForm({ ...form, siret: e.target.value })} className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-siret" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Email de contact</label>
              <input type="email" value={form.email_contact || ''} onChange={e => setForm({ ...form, email_contact: e.target.value })} className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Téléphone</label>
              <input value={form.telephone || ''} onChange={e => setForm({ ...form, telephone: e.target.value })} className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-telephone" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-1">Site web</label>
            <input value={form.site_web || ''} onChange={e => setForm({ ...form, site_web: e.target.value })} placeholder="www.kroquis.fr" className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]" data-testid="cabinet-site-web" />
          </div>

          {/* Aperçu mention légale */}
          {(form.forme_juridique || form.ordre_architectes || form.rcs || form.siege) && (
            <div className="bg-[#FAFAF8] border-l-4 border-[#C4773B] p-3 mt-4" data-testid="mention-preview">
              <p className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">Aperçu mention légale</p>
              <p className="text-[11px] text-[#4A4A4A] leading-relaxed italic">
                {[
                  form.nom && form.forme_juridique ? `${form.nom}, ${form.forme_juridique}` : form.nom,
                  form.ordre_architectes,
                ].filter(Boolean).join(' ').trim()}
                {form.ordre_architectes ? '. ' : form.nom ? '. ' : ''}
                {form.rcs && (form.siege || form.adresse)
                  ? `${form.rcs} et dont le siège social est enregistré au ${form.siege || form.adresse}.`
                  : form.rcs ? `${form.rcs}.` : (form.siege || form.adresse ? `Siège social : ${form.siege || form.adresse}.` : '')}
              </p>
            </div>
          )}
        </div>
        {/* RP35 / D5 — réglage cabinet « Droits du chef de projet » */}
        <div className="border-t border-[#E0E0DB] pt-4" data-testid="cabinet-droits-chef-de-projet">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">Droits du chef de projet</h3>
          <select
            value={form.droits_chef_de_projet || 'comme_architecte'}
            onChange={e => setForm({ ...form, droits_chef_de_projet: e.target.value })}
            className="w-full px-3 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C] bg-white"
            data-testid="cabinet-droits-chef-de-projet-select"
          >
            <option value="comme_architecte">Comme l'architecte, sauf supprimer le chantier et gérer l'équipe (recommandé)</option>
            <option value="restreints">Restreints : gère l'équipe, mais ne modifie ni la DPGF, ni le CCTP, ni les visites</option>
          </select>
        </div>
        <button onClick={handleSave} disabled={saving} className="w-full bg-[#2C2C2C] hover:bg-[#4A4A4A] text-white text-sm font-medium py-2 transition-colors disabled:opacity-50 mt-4" data-testid="cabinet-save-btn">
          {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </button>
      </div>
    </div>
  );
}
